export type ChatParticipante = {
    id_chat_conversa?: number
    id_usuario: number
    nome_usuario: string
    photo_usuario: string
    papel_participante: 'admin' | 'membro'
    id_ultima_mensagem_lida: number
}

export type ChatAnexo = {
    id_chat_anexo: number
    nome_original: string
    mime_type: string
    tamanho_bytes: number
}

export type ChatMensagemType = {
    id_chat_mensagem: number
    id_chat_conversa: number
    id_usuario_remetente: number
    tipo_mensagem: 'texto' | 'arquivo' | 'imagem' | 'sistema'
    conteudo_mensagem: string | null
    id_mensagem_resposta: number | null
    mencoes_mensagem: string | null
    editada_em: string | null
    chat_mensagem_ativa: number
    created_at: string
    nome_usuario: string
    photo_usuario: string
    anexos: ChatAnexo[]
}

export type ChatConversa = {
    id_chat_conversa: number
    tipo_conversa: 'direta' | 'grupo'
    nome_conversa: string | null
    id_usuario_criador: number
    id_processo_contexto: number | null
    ultima_mensagem_em: string | null
    ultima_mensagem_texto: string | null
    nao_lidas: number
    id_ultima_mensagem_lida: number
    papel_participante: 'admin' | 'membro'
    notificacoes_mudas: number
    participantes: ChatParticipante[]
    titulo_conversa: string
    foto_conversa: string | null
    id_usuario_destino: number | null
}

export type ChatContato = {
    id_usuario: number
    nome_usuario: string
    photo_usuario: string
    email_usuario: string
}

// ── Helpers puros ─────────────────────────────────────────────────────────────

export const CHAT_PERM_USAR = "110";
export const CHAT_PERM_GRUPO = "111";
export const CHAT_PERM_ADMIN = "112";

// Intervalos do polling adaptativo (ms)
export const POLL_ABERTO = 4000;
export const POLL_FECHADO = 20000;

export function iniciaisNome(nome: string) {
    const partes = String(nome || "?").trim().split(" ").filter(Boolean);
    if (partes.length === 0) return "?";
    if (partes.length === 1) return partes[0].substring(0, 2).toUpperCase();
    return `${partes[0][0]}${partes[partes.length - 1][0]}`.toUpperCase();
}

// Cor estável por usuário (mesma pessoa, mesma cor em qualquer tela)
const CORES_AVATAR = ['#1565c0', '#2e7d32', '#6a1b9a', '#c62828', '#ef6c00', '#00838f', '#4527a0', '#ad1457'];
export function corAvatar(id: number) {
    return CORES_AVATAR[Math.abs(Number(id) || 0) % CORES_AVATAR.length];
}

export function horaMensagem(data: string) {
    if (!data) return "";
    const d = new Date(String(data).replace(" ", "T"));
    if (isNaN(d.getTime())) return "";
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

// Rótulo da lista de conversas: hoje = hora, ontem, senão data curta
export function dataResumida(data: string | null) {
    if (!data) return "";
    const d = new Date(String(data).replace(" ", "T"));
    if (isNaN(d.getTime())) return "";
    const hoje = new Date();
    const mesmoDia = d.toDateString() === hoje.toDateString();
    if (mesmoDia) return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    const ontem = new Date(hoje);
    ontem.setDate(hoje.getDate() - 1);
    if (d.toDateString() === ontem.toDateString()) return "Ontem";
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

// Separador de dia dentro da conversa
export function rotuloDia(data: string) {
    if (!data) return "";
    const d = new Date(String(data).replace(" ", "T"));
    if (isNaN(d.getTime())) return "";
    const hoje = new Date();
    if (d.toDateString() === hoje.toDateString()) return "Hoje";
    const ontem = new Date(hoje);
    ontem.setDate(hoje.getDate() - 1);
    if (d.toDateString() === ontem.toDateString()) return "Ontem";
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function chaveDia(data: string) {
    if (!data) return "";
    const d = new Date(String(data).replace(" ", "T"));
    if (isNaN(d.getTime())) return "";
    return d.toDateString();
}

export function tamanhoLegivel(bytes: number) {
    const b = Number(bytes || 0);
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

export function isImagem(mime: string) {
    return String(mime || "").startsWith("image/");
}

// Busca sem diferenciar acento/caixa (mesmo padrão dos filtros do Kanban)
export function normalizar(texto: string) {
    return String(texto || "")
        .normalize("NFD")
        .split("")
        .filter(c => {
            const n = c.charCodeAt(0);
            return n < 0x300 || n > 0x36f;
        })
        .join("")
        .toLowerCase();
}

// ── Menções (@usuario) ────────────────────────────────────────────────────────

function escaparRegex(texto: string) {
    return String(texto || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Regex que casa "@Nome Completo" de qualquer participante.
// Nomes maiores primeiro: evita casar "Ana" dentro de "Ana Paula".
function regexMencoes(nomes: string[]) {
    const validos = nomes.map(n => String(n || "").trim()).filter(Boolean);
    if (validos.length === 0) return null;
    const alternativas = [...validos]
        .sort((a, b) => b.length - a.length)
        .map(escaparRegex)
        .join("|");
    // lookahead com \p{L} exige suporte a unicode property escapes; se faltar, cai no ASCII+acentos
    try {
        return new RegExp(`@(${alternativas})(?![\\p{L}\\p{N}])`, "giu");
    } catch {
        return new RegExp(`@(${alternativas})(?![A-Za-z\\u00C0-\\u024F0-9])`, "gi");
    }
}

// Ids dos participantes citados no texto — calculado no envio e salvo no banco
export function extrairMencoes(texto: string, participantes: ChatParticipante[]): number[] {
    const re = regexMencoes(participantes.map(p => p.nome_usuario));
    if (!re || !texto) return [];

    // sem Set: o target do tsconfig é es5 e não itera Set
    const ids: number[] = [];
    let achado: RegExpExecArray | null;
    while ((achado = re.exec(texto)) !== null) {
        const nomeCasado = achado[1];
        const alvo = participantes.find(p => normalizar(p.nome_usuario) === normalizar(nomeCasado));
        if (alvo && !ids.includes(Number(alvo.id_usuario))) ids.push(Number(alvo.id_usuario));
    }
    return ids;
}

// Quebra o texto em pedaços para o balão: { texto } puro ou { texto, nome } mencionado
export function segmentosMencao(texto: string, nomes: string[]): { texto: string, nome?: string }[] {
    const re = regexMencoes(nomes);
    if (!re || !texto) return [{ texto: String(texto || "") }];

    const partes: { texto: string, nome?: string }[] = [];
    let ultimo = 0;
    let achado: RegExpExecArray | null;
    while ((achado = re.exec(texto)) !== null) {
        if (achado.index > ultimo) partes.push({ texto: texto.substring(ultimo, achado.index) });
        partes.push({ texto: achado[0], nome: achado[1] });
        ultimo = achado.index + achado[0].length;
    }
    if (ultimo < texto.length) partes.push({ texto: texto.substring(ultimo) });
    return partes;
}

// Descobre o "@trecho" que está sendo digitado antes do cursor (null = não está mencionando)
export function mencaoEmDigitacao(texto: string, cursor: number) {
    const antes = String(texto || "").substring(0, cursor);
    const arroba = antes.lastIndexOf("@");
    if (arroba < 0) return null;

    // só vale @ no começo ou depois de espaço/quebra de linha
    const anterior = arroba > 0 ? antes[arroba - 1] : " ";
    if (!/\s/.test(anterior)) return null;

    const termo = antes.substring(arroba + 1);
    if (termo.length > 30 || /[\r\n]/.test(termo)) return null;
    // permite no máximo 2 palavras ("Ana Paula"), senão já é texto comum
    if (termo.trim().split(/\s+/).filter(Boolean).length > 2) return null;

    return { inicio: arroba, termo };
}
